import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { SidePanel } from '../../components/side-panel.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { pickLang } from '../../lib/pick-lang.js';

// #194: Nur-Lesen-Seitenleiste zum Testfall, geöffnet aus dem Defect-Detail rechts in „Meine Tests“
// (Fehler prüfen). Der Prüfer soll sehen, was getestet wurde, bevor er die Behebung bestätigt.
// Gleiche Query wie `ExecutionPanel` (`['tc', projectId, testCaseId]`) — der Cache wird geteilt.
// Escape/Schließen kommen vom SidePanel; hier gibt es keine Eingabe, die ein eigenes Escape bräuchte.
export function TestCasePanel({
  projectId,
  testCaseId,
  onClose,
}: {
  projectId: string;
  testCaseId: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';

  const {
    data: tc,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['tc', projectId, testCaseId],
    queryFn: () => catalogApi.getTestCase(projectId, testCaseId),
    enabled: Boolean(projectId && testCaseId),
  });

  const title = tc
    ? `${tc.human_id} · ${pickLang(lang, tc.title_de, tc.title_en)}`
    : isError
      ? t('common.loadFailed.title')
      : t('common.loading');
  const steps = (tc?.steps ?? []).slice().sort((a, b) => a.step_no - b.step_no);
  const firstTcode = steps.find((s) => s.tcode)?.tcode ?? null;
  const precondition = tc ? pickLang(lang, tc.precondition_de, tc.precondition_en) : '';
  const expected = tc ? pickLang(lang, tc.expected_de, tc.expected_en) : '';

  return (
    <SidePanel title={title} onClose={onClose}>
      {!tc && isError && <QueryErrorNotice onRetry={() => void refetch()} />}
      {tc && (
        <div className="space-y-4">
          {firstTcode && (
            <p className="text-xs text-text-muted">
              {t('catalog.fields.tcode')}: <span className="text-text-body">{firstTcode}</span>
            </p>
          )}
          {precondition && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-text-muted">
                {t('catalog.fields.precondition')}
              </p>
              <p className="whitespace-pre-wrap rounded-btn bg-bg-subtle px-2 py-1.5 text-sm text-text-body">
                {precondition}
              </p>
            </div>
          )}
          {expected && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-text-muted">
                {t('catalog.fields.expected')}
              </p>
              <p className="whitespace-pre-wrap rounded-btn bg-bg-subtle px-2 py-1.5 text-sm text-text-body">
                {expected}
              </p>
            </div>
          )}

          {steps.length === 0 ? (
            <p className="text-sm text-text-muted">{t('myDefects.testCaseNoSteps')}</p>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[11px] font-bold uppercase tracking-wide text-text-muted">
                  <th className="w-8 px-1.5 py-1 text-left">{t('catalog.fields.stepNo')}</th>
                  <th className="px-1.5 py-1 text-left">{t('catalog.fields.description')}</th>
                  <th className="w-20 px-1.5 py-1 text-left">{t('catalog.fields.tcode')}</th>
                  <th className="px-1.5 py-1 text-left">{t('catalog.fields.expectedOutput')}</th>
                </tr>
              </thead>
              <tbody>
                {steps.map((step) => (
                  <tr
                    key={step.step_id}
                    className="border-b border-[var(--border)] align-top last:border-0"
                  >
                    <td className="px-1.5 py-1 text-text-muted">{step.step_no}</td>
                    <td className="px-1.5 py-1 text-text-body">
                      {pickLang(lang, step.description_de, step.description_en)}
                    </td>
                    <td className="px-1.5 py-1 text-text-body">{step.tcode ?? '—'}</td>
                    <td className="px-1.5 py-1 text-text-body">
                      {pickLang(lang, step.expected_output_de, step.expected_output_en) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </SidePanel>
  );
}
